/**
 * Which sidebar structure this user sees.
 *
 * The old menu grew one entry at a time and reached fifteen top-level items for an admin, with
 * three loose work queues in the middle, a "Credit Control" submenu inside a "Credit Control"
 * submenu, and "Accounts" holding consultants, users and hospitals together. The new one groups
 * the queues under My Work, the lookup registers under Records, the two calendars under
 * Scheduling, and the guidance pages under Guidance. Invoice Management, Credit Control and
 * Extended Workflow are untouched.
 *
 * Both are built, and this decides which renders. A rearranged sidebar is the kind of change
 * people have to live with for a week before they can say whether it is better, and a toggle is
 * how they get to.
 */

export const NAV_OLD = 'old';
export const NAV_NEW = 'new';

export const NAV_STORAGE_KEY = 'navStructure';

/**
 * The old menu is the default deliberately.
 *
 * This branch merges to a release, and quietly rearranging the sidebar of every member of staff
 * because a trial was merged would be the wrong way round - they would find out by opening the
 * portal one morning. Anyone who wants the new one opts in from the sidebar, and the preference
 * sticks.
 *
 * Once it has been lived with, making it the default is changing this one constant.
 */
const DEFAULT_NAV = NAV_OLD;

const isKnown = (value) => value === NAV_OLD || value === NAV_NEW;

/**
 * localStorage is wrapped in try/catch throughout, not for tidiness: in a private window, or with
 * site data blocked, *reading* it throws rather than returning null. The sidebar renders on every
 * page in the portal, so an uncaught throw here would take the whole application down to lose a
 * preference that does not matter.
 */
export const getNavStructure = () => {
  try {
    const stored = window.localStorage.getItem(NAV_STORAGE_KEY);

    return isKnown(stored) ? stored : DEFAULT_NAV;
  } catch (e) {
    return DEFAULT_NAV;
  }
};

export const isNewNav = () => getNavStructure() === NAV_NEW;

/**
 * Stores a preference and returns what is now in effect, so a caller can set component state from
 * the return value rather than guessing whether the write landed.
 */
export const setNavStructure = (value) => {
  if (!isKnown(value)) {
    return getNavStructure();
  }

  try {
    window.localStorage.setItem(NAV_STORAGE_KEY, value);
  } catch (e) {
    // Blocked storage means the choice lasts for this page only. Better than a crash.
  }

  return value;
};

export const toggledNavStructure = (current) => (current === NAV_NEW ? NAV_OLD : NAV_NEW);
